import React, { useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiAlertTriangle, FiActivity, FiShield, FiTarget, FiZap, FiMessageSquare } = FiIcons;

// Importerer de korrekte, oppdaterte variablene fra logikk-filen
import { 
  CONFLICT_DIMENSIONS, 
  conflictTeamData, 
  getCRICategory, 
  getMediationActions 
} from '../data/conflictRiskLogic';

export const ConflictRiskIndex = () => {
  const [activeTab, setActiveTab] = useState('overview');
  
  // Sorterer team etter høyest risiko (CRI)
  const highRiskTeams = [...conflictTeamData].sort((a, b) => b.cri - a.cri);

  const heatmapOption = {
    tooltip: { position: 'top' },
    grid: { height: '70%', top: '10%' },
    xAxis: {
      type: 'category',
      data: conflictTeamData.map(t => t.name),
      splitArea: { show: true },
      axisLabel: { rotate: 45 }
    },
    yAxis: {
      type: 'category',
      data: CONFLICT_DIMENSIONS.map(d => d.name),
      splitArea: { show: true }
    },
    visualMap: {
      min: 0,
      max: 100,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: '5%',
      inRange: { color: ['#10b981', '#f59e0b', '#ef4444'] }
    },
    series: [{
      name: 'Risiko-score',
      type: 'heatmap',
      data: conflictTeamData.flatMap((t, tIdx) => 
        CONFLICT_DIMENSIONS.map((d, dIdx) => [tIdx, dIdx, t.scores[d.id]])
      ),
      label: { show: true },
      emphasis: {
        itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.5)' }
      }
    }]
  };

  return (
    <div className="space-y-8 pb-20">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Early Warning System</span>
        </div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight uppercase">Konfliktrisiko-analyse</h1>
        <p className="text-slate-500 font-medium font-inter">Identifisering av organisatoriske friksjonspunkter</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Heatmap Section */}
        <Card className="lg:col-span-2 border-none shadow-xl bg-white">
          <CardHeader 
            title="Konflikt-Heatmap" 
            subtitle="Risikofordeling på tvers av team og dimensjoner"
            icon={<SafeIcon icon={FiActivity} className="text-rose-600" />}
          />
          <CardContent className="h-[450px] p-6">
            <ReactECharts option={heatmapOption} style={{ height: '100%' }} />
          </CardContent>
        </Card>

        {/* High Risk Alerts */}
        <div className="space-y-6">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest px-2">Kritiske Observasjoner</h3>
          {highRiskTeams.slice(0, 3).map((team) => {
            const cat = getCRICategory(team.cri);
            return (
              <Card key={team.id} className={`border-none shadow-lg overflow-hidden border-l-4 ${team.cri > 50 ? 'border-l-rose-500' : 'border-l-amber-500'}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-black text-slate-900 uppercase text-sm tracking-tight">{team.name}</h4>
                      <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">Status: {cat.label}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-lg text-xs font-black ${team.cri > 50 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                      {team.cri}%
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-1000 ${team.cri > 50 ? 'bg-rose-500' : 'bg-amber-500'}`}
                      style={{ width: `${team.cri}%` }}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Mediation Section */}
        <Card className="border-none shadow-xl">
          <CardHeader 
            title="Meklingsstrategier" 
            subtitle="Anbefalte tiltak for høyrisiko-team"
            icon={<SafeIcon icon={FiZap} className="text-indigo-600" />}
          />
          <CardContent className="p-8 space-y-4">
            {getMediationActions(highRiskTeams[0]).mediation.map((action, i) => (
              <div key={i} className="flex gap-4 p-5 bg-indigo-50/50 rounded-2xl border border-indigo-100 group">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-lg shadow-indigo-200">
                  <SafeIcon icon={FiMessageSquare} size={18} />
                </div>
                <div>
                  <p className="text-sm text-slate-800 font-bold leading-snug">{action}</p>
                  <p className="text-[10px] text-indigo-600 font-black uppercase mt-1 tracking-wider">Prioritet: Høy</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Prevention Section */}
        <Card className="border-none shadow-xl">
          <CardHeader 
            title="Forebygging" 
            subtitle="Langsiktige strategier for risikoreduksjon"
            icon={<SafeIcon icon={FiShield} className="text-emerald-600" />}
          />
          <CardContent className="p-8 space-y-4">
            {getMediationActions(highRiskTeams[0]).prevention.map((action, i) => (
              <div key={i} className="flex gap-4 p-5 bg-slate-50 rounded-2xl border border-slate-100 group hover:border-emerald-500 transition-all">
                <div className="w-10 h-10 rounded-xl bg-slate-200 text-slate-600 flex items-center justify-center shrink-0 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                  <SafeIcon icon={FiTarget} size={18} />
                </div>
                <p className="text-sm text-slate-700 font-bold self-center leading-tight">{action}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConflictRiskIndex;